import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

export default function Login() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const user = localStorage.getItem('santa-helper-user');
    if (user) {
      navigate('/');
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        // Login logic
        if (email === 'admin@santashelper.com' && password === 'admin123') {
          // Admin login
          const adminUser = {
            id: 'admin',
            email: 'admin@santashelper.com',
            firstName: 'Admin',
            lastName: 'User',
            role: 'admin',
            loginTime: new Date().toISOString()
          };
          
          if (rememberMe) {
            localStorage.setItem('santa-helper-user', JSON.stringify(adminUser));
          } else {
            sessionStorage.setItem('santa-helper-user', JSON.stringify(adminUser));
          }
          
          navigate('/admin');
        } else {
          // Regular user login - check stored users
          const users = JSON.parse(localStorage.getItem('santa-helper-users') || '[]');
          const user = users.find((u: any) => u.email === email && u.password === password);
          
          if (user) {
            const loginUser = {
              ...user,
              loginTime: new Date().toISOString()
            };
            
            if (rememberMe) {
              localStorage.setItem('santa-helper-user', JSON.stringify(loginUser));
            } else {
              sessionStorage.setItem('santa-helper-user', JSON.stringify(loginUser));
            }
            
            navigate('/');
          } else {
            setError('Invalid email or password');
          }
        }
      } else {
        // Registration logic
        if (password !== confirmPassword) {
          setError('Passwords do not match');
          return;
        }
        
        if (password.length < 6) {
          setError('Password must be at least 6 characters');
          return;
        }
        
        // Check if user already exists
        const users = JSON.parse(localStorage.getItem('santa-helper-users') || '[]');
        const existingUser = users.find((u: any) => u.email === email);
        
        if (existingUser) {
          setError('User with this email already exists');
          return;
        }
        
        // Create new user
        const newUser = {
          id: Date.now().toString(),
          email,
          password,
          firstName,
          lastName,
          role: 'user',
          createdAt: new Date().toISOString(),
          wishlistItems: [],
          budget: {
            totalBudget: 1000,
            spent: 0,
            categories: {}
          }
        };
        
        users.push(newUser);
        localStorage.setItem('santa-helper-users', JSON.stringify(users));
        
        // Auto login after registration
        const loginUser = {
          ...newUser,
          loginTime: new Date().toISOString()
        };
        
        if (rememberMe) {
          localStorage.setItem('santa-helper-user', JSON.stringify(loginUser));
        } else {
          sessionStorage.setItem('santa-helper-user', JSON.stringify(loginUser));
        }
        
        navigate('/');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-green-50 to-red-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/9c6923d23b57a65ea10093d2d55f8147.jpg')`
        }}
      ></div>
      
      <Card className="w-full max-w-md relative z-10">
        <div className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-red-600 mb-2" style={{ fontFamily: '"Pacifico", serif' }}>
              🎅 Santa's Helper
            </h1>
            <p className="text-gray-600">
              {isLogin ? 'Welcome back to your Christmas wishlist!' : 'Join Santa\'s Helper today!'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="First name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Enter your email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Enter your password"
                required
              />
            </div>

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password *
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Confirm your password"
                  required
                />
              </div>
            )}

            {/* Remember Me */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500"
              />
              <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700">
                Keep me signed in
              </label>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  {isLogin ? 'Signing In...' : 'Creating Account...'}
                </div>
              ) : (
                <>
                  <i className={`${isLogin ? 'ri-login-box-line' : 'ri-user-add-line'} mr-2`}></i>
                  {isLogin ? 'Sign In' : 'Create Account'}
                </>
              )}
            </Button>
          </form>

          {/* Toggle Form */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError('');
                  setEmail('');
                  setPassword('');
                  setConfirmPassword('');
                  setFirstName('');
                  setLastName('');
                }}
                className="ml-2 text-red-600 hover:text-red-800 font-medium"
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}